import { NavigationProp, ParamListBase } from "@react-navigation/native";
import { omit } from "lodash";
import React from "react";
import {
  FlatList,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextProps,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewStyle,
} from "react-native";
import { useSelector } from "react-redux";
import { SLImage } from "src/components";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { PdStack, PdText } from "src/components/layout";
import { PdDivider, PdTableEmpty } from "src/components/layout/table";
import { PdSearchInput } from "src/components/ui";
import { useQuery } from "src/hooks/api";
import { useDebounce, useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import api from "src/util/apiNew";
import { includesIn } from "src/util/search";

// eslint-disable-next-line no-shadow
enum tProductStatus {
  Linked = "Linked",
  ForReview = "For Review",
  NotLinked = "Not Linked",
}

type tTableProduct = {
  id: string;
  uid: string;
  sku: string;
  name: string;
  status: tProductStatus;
  image?: string;
  shopifyInventoryItemId?: number;
  isVariant: boolean;
};

type tFilters = {
  status?: tProductStatus;
  term: string;
};

type tShopifyProduct = {
  inventoryItemId: number;
  name: string;
  sku: string;
  imageSrc: string | null;
};

type tLinkValue = {
  rowId: string;
  productId: string;
  variantSku: string | null;
  option: tPdSelectOption | null;
  inventoryItemId: number | null;
  shopifyMarkedAsNotLinked: boolean;
};

const getShopifyProducts = (): Promise<tShopifyProduct[]> =>
  api.get<tShopifyProduct[]>("shopify/products").then((res) => res.data);

const putShopifyLink = (
  data: {
    productId: string;
    variantSku?: string;
    shopifyInventoryItemId: number | null;
    shopifyMarkedAsNotLinked: boolean;
  }[],
) => api.put("products/shopify/link", data).then((res) => res.data);

const PdTab: React.FC<TouchableOpacityProps> = ({ style, ...rest }) => (
  <TouchableOpacity style={[styles.pdTab, style]} {...rest} />
);

const PdTabLabel: React.FC<{ isActive?: boolean } & TextProps> = ({ isActive, style, ...rest }) => (
  <PdText style={[isActive ? styles.pdTabLabelActive : {}, style]} {...rest} />
);

const Separator: React.FC = () => <PdDivider style={styles.zIndex_1} />;

const ProductItem: React.FC<{
  item: tTableProduct;
  value?: tLinkValue;
  shopifyItems?: tPdSelectOption[];
  onChange: (value: tLinkValue) => void;
  onDelete: (productId: string) => void;
}> = ({ value, item, shopifyItems, onChange, onDelete }) => {
  const defaultValue = React.useMemo(
    () => shopifyItems?.find((shopifyItem) => shopifyItem.value === `${item.shopifyInventoryItemId}`),
    [item.shopifyInventoryItemId, shopifyItems],
  );

  const handleOnChange = (option: tPdSelectOption | null) => {
    if (option)
      onChange({
        rowId: item.id,
        productId: item.uid,
        variantSku: item.isVariant ? item.sku : null,
        option,
        inventoryItemId: parseInt(option.value, 10),
        shopifyMarkedAsNotLinked: false,
      });
  };

  const handleOnPressDontLink = () => {
    onChange({
      rowId: item.id,
      productId: item.uid,
      variantSku: item.isVariant ? item.sku : null,
      option: null,
      inventoryItemId: null,
      shopifyMarkedAsNotLinked: true,
    });
  };

  const handleOnPressEdit = () => {
    onDelete(item.uid);
  };

  if (value?.inventoryItemId === null && value?.option === null) {
    return (
      <View style={[styles.flexRow, styles.alignCenter, styles.py2]}>
        <View style={[styles.flexRow, styles.alignCenter, styles.flex1]}>
          <SLImage source={{ uri: item.image }} mobileStyle={styles.image} webStyle={webStyles.image} />
          <View style={[styles.flex1, styles.ml2]}>
            <PdText>{item.name}</PdText>
            <PdText style={styles.textBold}>{`SKU: ${item.sku}`}</PdText>
          </View>
        </View>
        <View style={styles.flex1}>
          <PdText>Won&apos;t link to Shopify listing</PdText>
        </View>
        <PdButton variant="ghost" onPress={handleOnPressEdit}>
          <PdButtonLabel>Edit</PdButtonLabel>
        </PdButton>
      </View>
    );
  }

  return (
    <View style={[styles.flexRow, styles.alignCenter, styles.py2]}>
      <View style={[styles.flexRow, styles.alignCenter, styles.flex1]}>
        <SLImage source={{ uri: item.image }} mobileStyle={styles.image} webStyle={webStyles.image} />
        <View style={[styles.flex1, styles.ml2]}>
          <PdText>{item.name}</PdText>
          <PdText style={styles.textBold}>{`SKU: ${item.sku}`}</PdText>
        </View>
      </View>
      <View style={styles.flex1}>
        <PdSelect
          containerStyle={styles.flex1}
          value={value?.option || defaultValue}
          onChange={handleOnChange}
          options={shopifyItems || []}
          isClearable={false}
        />
      </View>
      <PdButton variant="ghost" onPress={handleOnPressDontLink}>
        <PdButtonLabel>Don&apos;t Link</PdButtonLabel>
      </PdButton>
    </View>
  );
};

const useTableData = (filters: tFilters) => {
  const dTermFilter = useDebounce(filters.term, 500);
  const rProducts = useSelector((state) => state.BrandProductReducer);
  const totalsRef = React.useRef({ linked: 0, forReview: 0, notLinked: 0 });
  const data = React.useMemo(() => {
    totalsRef.current = { linked: 0, forReview: 0, notLinked: 0 };
    return rProducts.reduce<tTableProduct[]>((result, product) => {
      if (product.variants.length > 0) {
        for (const variant of product.variants) {
          const variantRow = {
            id: `${product.uid}_${variant.sku}`,
            uid: product.uid,
            sku: variant.sku,
            name: `${product.name} ${variant.options.map((option) => option.label).join(" ")}`,
            status: variant.shopifyMarkedAsNotLinked
              ? tProductStatus.NotLinked
              : !variant.shopifyInventoryItemId
              ? tProductStatus.ForReview
              : tProductStatus.Linked,
            image: variant.imageUrl,
            shopifyInventoryItemId: variant.shopifyInventoryItemId,
            isVariant: true,
          };

          const variantStatusFilter = !filters.status || (filters.status && filters.status === variantRow.status);
          const variantTermFilter = !dTermFilter || includesIn(variantRow, dTermFilter);

          if (variantTermFilter) {
            totalsRef.current = {
              linked:
                variantRow.status === tProductStatus.Linked ? totalsRef.current.linked + 1 : totalsRef.current.linked,
              forReview:
                variantRow.status === tProductStatus.ForReview
                  ? totalsRef.current.forReview + 1
                  : totalsRef.current.forReview,
              notLinked:
                variantRow.status === tProductStatus.NotLinked
                  ? totalsRef.current.notLinked + 1
                  : totalsRef.current.notLinked,
            };
          }

          if (variantStatusFilter && variantTermFilter) {
            result.push(variantRow);
          }
        }
      } else {
        const row = {
          id: product.uid,
          uid: product.uid,
          sku: product.sku,
          name: product.name,
          status: product.shopifyMarkedAsNotLinked
            ? tProductStatus.NotLinked
            : !product.shopifyInventoryItemId
            ? tProductStatus.ForReview
            : tProductStatus.Linked,
          image: product.images?.[0]?.image_url,
          shopifyInventoryItemId: product.shopifyInventoryItemId,
          isVariant: false,
        };

        const statusFilter = !filters.status || (filters.status && filters.status === row.status);
        const termFilter = !dTermFilter || includesIn(row, dTermFilter);

        if (termFilter) {
          totalsRef.current = {
            linked: row.status === tProductStatus.Linked ? totalsRef.current.linked + 1 : totalsRef.current.linked,
            forReview:
              row.status === tProductStatus.ForReview ? totalsRef.current.forReview + 1 : totalsRef.current.forReview,
            notLinked:
              row.status === tProductStatus.NotLinked ? totalsRef.current.notLinked + 1 : totalsRef.current.notLinked,
          };
        }

        if (statusFilter && termFilter) {
          result.push(row);
        }
      }

      return result;
    }, []);
  }, [dTermFilter, filters.status, rProducts]);

  return { data, totals: totalsRef.current };
};

const P08_02_ShopifyIntegration: React.FC<{ navigation: NavigationProp<ParamListBase> }> = () => {
  const [filters, setFilters] = React.useState<tFilters>({
    status: tProductStatus.Linked,
    term: "",
  });
  const isMobile = useIsMobile();
  const table = useTableData(filters);
  const { data } = useQuery(getShopifyProducts);

  const [linkProducts, setLinkProducts] = React.useState<{ [productId: string]: tLinkValue }>({});

  const shopifyItems = React.useMemo(
    () =>
      data?.map((shopifyItem) => ({
        value: `${shopifyItem.inventoryItemId}`,
        label: `${shopifyItem.name} - ${shopifyItem.sku}`,
      })),
    [data],
  );

  const addProductToLink = (value: tLinkValue) => {
    setLinkProducts((prev) => ({ ...prev, [value.rowId]: value }));
  };

  const deleteProductToLink = (productId: string) => {
    setLinkProducts((prev) => omit(prev, productId));
  };

  const handleOnPressConfirm = async () => {
    const payload: {
      productId: string;
      variantSku?: string;
      shopifyInventoryItemId: number | null;
      shopifyMarkedAsNotLinked: boolean;
    }[] = [];
    Object.keys(linkProducts).forEach((key) => {
      const current = linkProducts[key];
      payload.push({
        productId: current.productId,
        variantSku: current.variantSku || undefined,
        shopifyInventoryItemId: current.inventoryItemId,
        shopifyMarkedAsNotLinked: current.shopifyMarkedAsNotLinked,
      });
    });
    await putShopifyLink(payload);
  };

  return (
    <View style={styles.flex1}>
      <View style={[isMobile ? styles.px4 : styles.px10, styles.pt8, styles.pb2, styles.zIndex1]}>
        <PdText variant="subheader" style={styles.mb4}>
          Shopify Integration
        </PdText>
        <PdStack
          direction={isMobile ? "vertical" : "horizontal"}
          style={[styles.justifyBetween, styles.mb4, isMobile ? styles.widthFull : {}]}
        >
          <PdSearchInput
            style={styles.flex1}
            value={filters.term}
            onChangeText={(newTerm) => setFilters((prev) => ({ ...prev, term: newTerm }))}
            placeholder="Search your products"
          />
          <View style={styles.flex1}>
            <PdButton style={isMobile ? {} : styles.mlAuto} onPress={handleOnPressConfirm}>
              <PdButtonLabel>Confirm & Finish</PdButtonLabel>
            </PdButton>
          </View>
        </PdStack>
        <View style={[styles.flexRow, styles.alignCenter, styles.widthFull]}>
          <ScrollView horizontal>
            <PdTab onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.Linked }))}>
              <PdTabLabel
                isActive={filters.status === tProductStatus.Linked}
              >{`Linked ${table.totals.linked}`}</PdTabLabel>
            </PdTab>
            <PdTab onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.ForReview }))}>
              <PdTabLabel
                isActive={filters.status === tProductStatus.ForReview}
              >{`For Review ${table.totals.forReview}`}</PdTabLabel>
            </PdTab>
            <PdTab onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.NotLinked }))}>
              <PdTabLabel
                isActive={filters.status === tProductStatus.NotLinked}
              >{`Not Linked ${table.totals.notLinked}`}</PdTabLabel>
            </PdTab>
          </ScrollView>
        </View>
      </View>
      <FlatList
        ListEmptyComponent={PdTableEmpty}
        ItemSeparatorComponent={Separator}
        data={table.data}
        keyExtractor={(item) => item.sku}
        CellRendererComponent={({ children, index, style, ...props }) => {
          const cellStyle = [style, { zIndex: table.data.length - index }];
          return (
            <View style={cellStyle} index={index} {...props}>
              {children}
            </View>
          );
        }}
        renderItem={({ item }) => (
          <ProductItem
            item={item}
            value={linkProducts[`${item.id}`]}
            shopifyItems={shopifyItems}
            onChange={addProductToLink}
            onDelete={deleteProductToLink}
          />
        )}
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        style={Platform.OS === "web" ? (webStyles.overflowAuto as StyleProp<ViewStyle>) : {}}
        contentContainerStyle={styles.container}
      />
    </View>
  );
};

export const P08_02 = React.memo(P08_02_ShopifyIntegration);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  container: {
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  image: {
    backgroundColor: kColors.darkGray,
    height: 30,
    width: 30,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  pb2: {
    paddingBottom: kStyleConst.kSpacing * 2,
  },
  pdTab: {
    paddingRight: kStyleConst.kSpacing * 6,
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  pdTabLabelActive: {
    fontWeight: "600",
  },
  pt8: {
    paddingTop: kStyleConst.kSpacing * 8,
  },
  px10: {
    paddingHorizontal: kStyleConst.kSpacing * 10,
  },
  px4: {
    paddingHorizontal: kStyleConst.kSpacing * 4,
  },
  py2: {
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  textBold: {
    fontWeight: "500",
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
  zIndex_1: {
    zIndex: -1,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  overflowAuto: {
    overflow: "auto",
  },
  image: {
    backgroundColor: kColors.darkGray,
    height: 30,
    width: 30,
  },
} as const;
